import { unwrap, type components } from "@sagot/api-client";
import { useMutation, useQueries, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { dkKeys } from "../shared/keys";
import type { ChannelOut, ChannelType } from "../shared/types";
import type { ReplyMode } from "./channels-model";

export type AnalyticsRange = "7d" | "30d";
export type SummaryOut = components["schemas"]["SummaryOut"];
type WebhookOut = components["schemas"]["WebhookOut"];

export function useChannels(agentId: string): UseQueryResult<ChannelOut[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: dkKeys.channels(wsId, agentId),
    queryFn: () => unwrap(api.GET("/v1/agents/{agent_id}/channels", { params: { path: { agent_id: agentId } } })),
  });
}

/** Replaces one channel in the cached list with the server's answer. */
function useStoreChannel(agentId: string) {
  const queryClient = useQueryClient();
  const wsId = useWorkspaceId();
  return (updated: ChannelOut) => {
    queryClient.setQueryData<ChannelOut[]>(dkKeys.channels(wsId, agentId), (rows) => rows?.map((row) => (row.type === updated.type ? updated : row)));
  };
}

export function useToggleChannel(agentId: string) {
  const api = useApi();
  const store = useStoreChannel(agentId);
  return useMutation({
    mutationFn: ({ type, enabled }: { type: ChannelType; enabled: boolean }) =>
      unwrap(api.PATCH("/v1/agents/{agent_id}/channels/{channel_type}", { params: { path: { agent_id: agentId, channel_type: type } }, body: { enabled } })),
    onSuccess: store,
  });
}

export interface EmailSettings {
  enabled: boolean;
  replyMode: ReplyMode;
  forwardingAddress: string | null;
}

export function useConfigureEmail(agentId: string) {
  const api = useApi();
  const store = useStoreChannel(agentId);
  return useMutation({
    mutationFn: (settings: EmailSettings) =>
      unwrap(
        api.PUT("/v1/agents/{agent_id}/channels/email", {
          params: { path: { agent_id: agentId } },
          body: { enabled: settings.enabled, reply_mode: settings.replyMode, forwarding_address: settings.forwardingAddress },
        }),
      ),
    onSuccess: store,
  });
}

export function useMessengerConnect(agentId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/v1/agents/{agent_id}/channels/messenger/connect", { params: { path: { agent_id: agentId } } })),
  });
}

export function useMessengerDisconnect(agentId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const wsId = useWorkspaceId();
  return useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/agents/{agent_id}/channels/messenger", { params: { path: { agent_id: agentId } } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: dkKeys.channels(wsId, agentId) }),
  });
}

function summaryQuery(api: ReturnType<typeof useApi>, agentId: string, range: AnalyticsRange, channel?: ChannelType) {
  return () => unwrap(api.GET("/v1/analytics/summary", { params: { query: { agent_id: agentId, range, ...(channel ? { channel } : {}) } } }));
}

/** Agent-wide summary (KPI deltas, average response). */
export function useAgentSummary(agentId: string, range: AnalyticsRange): UseQueryResult<SummaryOut> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: [...dkKeys.channelAnalytics(wsId, agentId, range), "all"],
    queryFn: summaryQuery(api, agentId, range),
    retry: false,
  });
}

/** One summary per channel for the performance table (`GET /v1/analytics/summary?channel=`). */
export function useChannelSummaries(agentId: string, range: AnalyticsRange, types: readonly ChannelType[]): UseQueryResult<SummaryOut>[] {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQueries({
    queries: types.map((type) => ({
      queryKey: [...dkKeys.channelAnalytics(wsId, agentId, range), type],
      queryFn: summaryQuery(api, agentId, range, type),
      retry: false,
    })),
  });
}

/** Workspace webhooks (count only); optional — hidden when the member can't list them. */
export function useWebhookCount(): UseQueryResult<WebhookOut[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: ["ws", wsId, "webhooks"], queryFn: () => unwrap(api.GET("/v1/webhooks")), retry: false, staleTime: 60_000 });
}
